/**
 * Flight Review & Launch Component
 * 
 * Step 2 of Planned Flight flow
 * Shows flight summary and pre-flight status checklist
 */

import { FC } from 'react';
import { CheckSquare, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PreFlightCheck {
    id: string;
    label: string;
    status: string;
    passed: boolean;
}

interface FlightReviewLaunchProps {
    flightName?: string;
    missionName?: string;
    onLaunch: () => void;
    isLaunching?: boolean;
}

const preFlightChecks: PreFlightCheck[] = [
    { id: 'critical', label: 'All critical checks passed', status: '', passed: true },
    { id: 'weather', label: 'Weather conditions', status: 'Suitable', passed: true },
    { id: 'airspace', label: 'Airspace clearance', status: 'Granted', passed: true },
    { id: 'battery', label: 'Battery level', status: 'Sufficient', passed: true },
];

export const FlightReviewLaunch: FC<FlightReviewLaunchProps> = ({
    onLaunch,
    isLaunching = false,
}) => {
    return (
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-8 w-full">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-lg font-medium text-white mb-1">Flight Review & Launch</h2>
                <p className="text-slate-400 text-sm">Verify all details before authorizing flight</p>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-2 gap-6">
                {/* Left: Flight Summary / Map Placeholder */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg h-48 flex items-center justify-center">
                    <span className="text-slate-500 text-sm">Flight Summary Map</span>
                </div>

                {/* Right: Pre-Flight Status */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                    <h3 className="text-cyan-400 text-sm font-medium mb-4">Pre-Flight Status</h3>
                    <div className="space-y-3">
                        {preFlightChecks.map((check) => (
                            <div key={check.id} className="flex items-center gap-3">
                                <CheckSquare 
                                    className={`w-4 h-4 ${check.passed ? 'text-cyan-400' : 'text-red-400'}`} 
                                />
                                <span className="text-slate-300 text-sm">
                                    {check.label}
                                    {check.status && `: ${check.status}`}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-end mt-8">
                <Button
                    type="button"
                    onClick={onLaunch}
                    disabled={isLaunching}
                    className="disabled:bg-slate-700 disabled:text-slate-400"
                >
                    {isLaunching ? 'Launching...' : 'Continue to Pre-Flight Check'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
            </div>
        </div>
    );
};
